import { useEffect, useRef } from "react";

interface UseAnswerOutdatedOptions {
  /** The message the answer belongs to. Switching it forgets any pending change. */
  messageId: string | null | undefined;
  /** An answer is on screen (nothing to flag on an empty page). */
  answerPresent: boolean;
  /** A generation is in flight from this screen's point of view. */
  generationRunning: boolean;
  /**
   * The server's verdict (`Message.answerOutdated`): the answer shown no longer
   * matches the facts, chat files and machine it should be built from.
   * `undefined`/`null` while the field has not been loaded.
   */
  outdatedFromServer: boolean | null | undefined;
  /**
   * The local "facts changed since the last generation" flag. The caller owns
   * the state (it is read much earlier than this hook can run, by the
   * regenerate-CTA logic) and sets it when the operator edits a fact.
   */
  factsChanged: boolean;
  clearFactsChanged: () => void;
}

/**
 * Tracks whether the answer on screen is outdated, for every surface that shows
 * a proposed answer (Neptune's answer tab, Venus' agent page).
 *
 * Two signals feed it. The server's `answerOutdated` is authoritative but only
 * arrives with the next refetch; the caller's local "facts changed" flag, set the
 * moment the operator edits a fact, covers the gap until then.
 *
 * The local flag is dropped only when a generation has settled AND the server
 * says the answer matches the inputs. A run finishing is not enough: one that
 * started before the latest change finishes with the old inputs, and the server
 * discards or supersedes it, so clearing on "running -> idle" alone let an
 * outdated answer pass as current.
 */
export function useAnswerOutdated({
  messageId,
  answerPresent,
  generationRunning,
  outdatedFromServer,
  factsChanged,
  clearFactsChanged,
}: UseAnswerOutdatedOptions) {
  const prevRunningRef = useRef(generationRunning);
  const settledSinceChangeRef = useRef(false);

  useEffect(() => {
    settledSinceChangeRef.current = false;
  }, [messageId]);

  useEffect(() => {
    if (prevRunningRef.current && !generationRunning) {
      settledSinceChangeRef.current = true;
    }
    prevRunningRef.current = generationRunning;
    if (
      settledSinceChangeRef.current &&
      !generationRunning &&
      outdatedFromServer === false
    ) {
      settledSinceChangeRef.current = false;
      clearFactsChanged();
    }
  }, [generationRunning, outdatedFromServer, clearFactsChanged]);

  // Never while a run is in flight (the loading surfaces already say so).
  const outdatedShown =
    answerPresent &&
    !generationRunning &&
    (outdatedFromServer === true || factsChanged);

  return outdatedShown;
}
